import { FC, useEffect, useMemo } from 'react';
import {
  Button,
  DataLossPrompt,
  FileInput,
  FileLink,
  Footer,
  ImageList,
  MultiLanguageInputContainer,
  Panel,
  RichTextInput,
  SelectInput,
  SortableList,
  TextInput,
} from 'melp-design/components';
import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ContentLanguage } from 'types/general';
import { useNew, useUpdateNew } from 'store/news';
import { ROUTES } from 'config';
import {
  Control,
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form-latest';
import { Translations } from 'types/Common';
import { useCompanyLanguages, useMe } from 'state/Administrators';
import { useRouteParams } from 'utils/useIdParam';
import { formatLanguage } from 'utils/format';
import { useUploadFile } from 'store/files';
import { Delete } from '@mui/icons-material';
import { SystemColors } from 'melp-design/style';
import { usePredefinedToasts } from 'utils/Toast';
import { resolveTranslation, stripHtml } from 'utils/general';

interface Values {
  languages: Array<Uppercase<ContentLanguage>>;
  translations: Translations<{
    title: string;
    content: string;
    imageId: string;
    attachments: Array<{ attachmentId: string }>;
  }>;
}

const getInitialValues = (
  defaultLanguage: Uppercase<ContentLanguage>,
): Values => ({
  languages: [defaultLanguage],
  translations: [
    {
      language: defaultLanguage,
      title: '',
      content: '',
      imageId: '',
      attachments: [],
    },
  ],
});

const AttachmentsList: FC<{
  control: Control<Values>;
  translationIndex: number;
  disabled?: boolean;
}> = ({ control, translationIndex, disabled }) => {
  const { t } = useTranslation();

  const {
    fields: attachmentFields,
    append: appendAttachment,
    move: moveAttachments,
    remove: removeAttachment,
  } = useFieldArray({
    control,
    name: `translations.${translationIndex}.attachments`,
  });

  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();

  return (
    <Controller
      name={`translations.${translationIndex}.attachments`}
      control={control}
      render={({ field: { name, value, onChange, ref } }) => (
        <Stack gap={1}>
          <FileInput
            name={name}
            onChange={([file]) => {
              if (file) {
                uploadFile(
                  { file },
                  {
                    onSuccess: ({ data }) => {
                      appendAttachment({ attachmentId: data.id });
                    },
                  },
                );
              } else {
                onChange(value);
              }
            }}
            disabled={isUploading || disabled}
            maxFiles={1}
            previewSelectedFiles={false}
            accept={[
              'jpg',
              'jpeg',
              'png',
              'gif',
              'webp',
              'txt',
              'pdf',
              'doc',
              'docx',
              'xls',
              'xlsx',
            ]}
            ref={ref}
          />

          <SortableList
            key={JSON.stringify(attachmentFields.map((field) => field.id))}
            onPositionChange={moveAttachments}
            disabled={disabled}
            items={attachmentFields.map((field, i) => ({
              id: field.id,
              content: (
                <Stack
                  direction="row"
                  alignItems="center"
                  py={0.5}
                  px={2}
                  spacing={2}
                  sx={{ backgroundColor: SystemColors.white }}
                  borderRadius={1}
                  boxShadow={`0 0 0 1px ${SystemColors.grey[90]}`}
                  key={field.id}
                >
                  <SortableList.DragIndicator />

                  <Box flex={1}>
                    <FileLink id={field.attachmentId} />
                  </Box>

                  <Box flexShrink={0}>
                    <Button
                      variant="neutral-outline"
                      size="md"
                      icon={Delete}
                      onClick={() => removeAttachment(i)}
                      title={t('common.remove')}
                      disabled={disabled}
                    />
                  </Box>
                </Stack>
              ),
            }))}
          />
        </Stack>
      )}
    />
  );
};

interface Props {
  onSaveSuccess: () => void;
}

export const NewContent: FC<Props> = ({ onSaveSuccess }) => {
  const { t, i18n } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { id: newId } = useRouteParams(ROUTES.news.details);
  const { defaultLanguage, supportedLanguages } = useCompanyLanguages();
  const { isFeatureEnabled, hasEditPermission } = useMe();
  const canEdit = hasEditPermission(['news']);

  const INITIAL_VALUES = useMemo(
    () => getInitialValues(defaultLanguage),
    [defaultLanguage],
  );

  const { data: newItem } = useNew(newId);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { dirtyFields, isSubmitted },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });
  const { fields: translationsFields, replace: replaceTranslations } =
    useFieldArray({ name: 'translations', control });

  useEffect(() => {
    if (newItem && newItem.translations.length) {
      reset({
        languages: newItem.translations.map((trans) => trans.language),
        translations: newItem.translations.map((trans) => ({
          language: trans.language,
          title: trans.title,
          content: trans.content,
          imageId: trans.headerImage?.id ?? '',
          attachments: trans.attachments.map((attachment) => ({
            attachmentId: attachment.id,
          })),
        })),
      });
    }
  }, [newItem, reset]);

  const { mutate: updateNew, isLoading: isUpdating } = useUpdateNew();
  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateNew(
      {
        newId,
        update: {
          translations: values.translations.map((trans) => ({
            language: trans.language,
            title: trans.title,
            content: trans.content,
            headerImageId: trans.imageId || null,
            attachmentIds: trans.attachments.map(
              (attachment) => attachment.attachmentId,
            ),
          })),
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
          onSaveSuccess();
        },
      },
    );
  };

  const [languages] = watch(['languages']);
  const isDirty = !!Object.keys(dirtyFields).length && !isSubmitted;
  const translation = resolveTranslation(newItem?.translations ?? []);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Panel title={t('benefitTemplates.templateContent')}>
            <Stack spacing={2}>
              <Controller
                name="languages"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <SelectInput
                    label={t('companies.supported_languages')}
                    name={name}
                    value={value}
                    onChange={(value) => {
                      const selectedLanguages = value as Array<
                        Uppercase<ContentLanguage>
                      >;
                      replaceTranslations(
                        selectedLanguages.map(
                          (language) =>
                            getValues('translations').find(
                              (t) => t.language === language,
                            ) ?? {
                              ...INITIAL_VALUES.translations[0],
                              language,
                            },
                        ),
                      );
                      onChange(selectedLanguages);
                    }}
                    options={supportedLanguages.map((language) => ({
                      value: language,
                      label: formatLanguage(i18n.language, language),
                      disabled: language === defaultLanguage,
                    }))}
                    multiple
                    required
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              {languages.length ? (
                <>
                  <MultiLanguageInputContainer
                    label={t('common.title')}
                    required
                    fields={translationsFields.map((field, i) => ({
                      id: field.id,
                      language: field.language,
                      component: (
                        <Controller
                          name={`translations.${i}.title`}
                          control={control}
                          render={({
                            field: { name, value, onChange, ref },
                          }) => (
                            <TextInput
                              name={name}
                              value={value}
                              onChange={onChange}
                              required
                              ai={
                                isFeatureEnabled('ai')
                                  ? {
                                      inputLanguage: defaultLanguage,
                                      outputLanguage: field.language,
                                      text:
                                        getValues('translations').find(
                                          (t) => t.language === defaultLanguage,
                                        )?.title ?? '',
                                    }
                                  : undefined
                              }
                              disabled={!canEdit}
                              ref={ref}
                            />
                          )}
                        />
                      ),
                    }))}
                  />

                  <MultiLanguageInputContainer
                    label={t('form.image')}
                    disabled={!canEdit}
                    onIsSingleChange={(isSingle) => {
                      if (!isSingle) return;

                      const defaultField = getValues('translations').find(
                        (field) => field.language === defaultLanguage,
                      );

                      if (defaultField) {
                        replaceTranslations(
                          getValues('translations').map((field) => ({
                            ...field,
                            imageId: defaultField.imageId,
                          })),
                        );
                      }
                    }}
                    fields={(isSingle) =>
                      translationsFields.map((field, i) => ({
                        id: field.id,
                        language: field.language,
                        component: (
                          <Controller
                            name={`translations.${i}.imageId`}
                            control={control}
                            render={({
                              field: { name, value, onChange, ref },
                            }) => {
                              return value ? (
                                <ImageList
                                  items={[
                                    {
                                      imageId: value,
                                      actions: [
                                        {
                                          icon: Delete,
                                          variant: 'neutral-outline',
                                          disabled: !canEdit,
                                          onClick: () => {
                                            isSingle
                                              ? replaceTranslations(
                                                  translationsFields.map(
                                                    (field) => ({
                                                      ...field,
                                                      imageId: '',
                                                    }),
                                                  ),
                                                )
                                              : onChange('');
                                          },
                                        },
                                      ],
                                    },
                                  ]}
                                />
                              ) : (
                                <FileInput
                                  name={name}
                                  onChange={([file]) => {
                                    if (file) {
                                      uploadFile(
                                        { file },
                                        {
                                          onSuccess: ({ data }) => {
                                            isSingle
                                              ? replaceTranslations(
                                                  translationsFields.map(
                                                    (field) => ({
                                                      ...field,
                                                      imageId: data.id,
                                                    }),
                                                  ),
                                                )
                                              : onChange(data.id);
                                          },
                                        },
                                      );
                                    } else {
                                      onChange('');
                                    }
                                  }}
                                  disabled={isUploading || !canEdit}
                                  maxFiles={1}
                                  previewSelectedFiles={false}
                                  accept={['jpg', 'jpeg', 'png', 'gif', 'webp']}
                                  ai={
                                    isFeatureEnabled('ai')
                                      ? {
                                          title: translation?.title,
                                          description: translation?.content
                                            ? stripHtml(translation.content)
                                            : undefined,
                                          aspectRatio: [16, 9],
                                        }
                                      : undefined
                                  }
                                  ref={ref}
                                />
                              );
                            }}
                          />
                        ),
                      }))
                    }
                  />

                  <MultiLanguageInputContainer
                    label={t('form.news_text')}
                    required
                    fields={translationsFields.map((field, i) => ({
                      id: field.id,
                      language: field.language,
                      component: (
                        <Controller
                          name={`translations.${i}.content`}
                          control={control}
                          render={({ field: { value, onChange, ref } }) => (
                            <RichTextInput
                              value={value}
                              onChange={onChange}
                              minHeight={250}
                              maxHeight={500}
                              ai={
                                isFeatureEnabled('ai')
                                  ? {
                                      inputLanguage: defaultLanguage,
                                      outputLanguage: field.language,
                                      text:
                                        getValues('translations').find(
                                          (t) => t.language === defaultLanguage,
                                        )?.content ?? '',
                                    }
                                  : undefined
                              }
                              disabled={!canEdit}
                              ref={ref}
                            />
                          )}
                        />
                      ),
                    }))}
                  />
                </>
              ) : null}
            </Stack>
          </Panel>

          <Panel title={t('form.attachments')}>
            <Stack spacing={2}>
              <MultiLanguageInputContainer
                fields={translationsFields.map((field, i) => ({
                  id: field.id,
                  language: field.language,
                  component: (
                    <AttachmentsList
                      control={control}
                      translationIndex={i}
                      disabled={!canEdit}
                    />
                  ),
                }))}
              />
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty}>
          <Stack direction="row" justifyContent="end" gap="10px">
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />
            <Button
              label={t('common.save')}
              variant="secondary"
              type="submit"
              loading={isUpdating}
              disabled={!canEdit}
            />
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={!hasEditPermission(['news']) ? false : isDirty} />
    </>
  );
};
